import { parseCpf } from '@brazilian-utils/brazilian-utils';
import { DEFAULT_BRAND } from '@payments-checkout/shared/helpers/constants/brand';
import { StringManager } from '@payments-checkout/shared/helpers/managers/string.manager';
import { BillingInformationFormFields } from '../../../app/types/billingInformation.types';
import {
  buildPaymentPayload,
  PaymentMethodDetails,
} from '../../../hooks/usePayment/buildPaymentPayload';
import { AddressFields } from '../../../types/address';
import { acceptedPaymentMethod } from '../../../types/customer.types';

export function isCreditCard(paymentMethod: string | null) {
  return paymentMethod === 'credit_card';
}

export const adapterAddressFields = (
  data: BillingInformationFormFields
): { address: AddressFields } => {
  const zipCode = data['billing-address-zip-code'];
  const city = data['billing-address-city'];
  const state = data['billing-address-state'];
  const street = data['billing-address-street'];
  const district = data['billing-address-district'];
  const number = data['billing-address-number'];
  const complement = data['billing-address-complement'] || '';
  const country = data['billing-address-country']?.toString() ?? 'BR';

  const address = {
    zip_code: StringManager.formatZipCode(zipCode ?? '', country),
    city,
    state,
    street,
    neighborhood: district,
    number: StringManager.filledString(number) ?? 'S/N',
    complement: StringManager.filledString(complement),
    country,
  };
  return { address };
};

export const adapterShippingAddressFields = (
  data: BillingInformationFormFields
): { address: AddressFields } => {
  const zipCode = data['shipping-address-zip-code'];
  const city = data['shipping-address-city'];
  const state = data['shipping-address-state'];
  const street = data['shipping-address-street'];
  const district = data['shipping-address-district'];
  const number = data['shipping-address-number'];
  const complement = data['shipping-address-complement'] || '';
  const country = data['shipping-address-country']?.toString() ?? 'BR';

  const address = {
    zip_code: StringManager.formatZipCode(zipCode ?? '', country),
    city,
    state,
    street,
    neighborhood: district,
    number: StringManager.filledString(number) ?? 'S/N',
    complement: StringManager.filledString(complement),
    country,
  };
  return { address };
};

export function getTokenizeCardParam(
  cardNumber: string | null,
  cardHolder: string | null,
  expirationDate: string | null,
  cvv: string | null
) {
  const date = expirationDate?.split('/');

  return {
    type: 'card',
    card: {
      number: cardNumber?.replace(/\s/g, ''),
      holder_name: cardHolder,
      exp_month: date && date[0],
      exp_year: date && date[1],
      cvv: cvv,
    },
  };
}

export function getPaymentParam(
  cardToken: string,
  paymentMethod: acceptedPaymentMethod,
  installments: number | null,
  antifraudId: string | null,
  tdsData: any,
  useBrandInterestRate?: boolean,
  brand?: string,
  quoteId?: string,
  isDeliveryEnabled?: boolean,
  riskId?: string
) {
  const details: PaymentMethodDetails = {};

  if (isCreditCard(paymentMethod)) {
    const creditCard = {
      token_id: cardToken,
      installments: installments,
      antifraud_id: antifraudId,
    };

    if (useBrandInterestRate) {
      Object.assign(creditCard, {
        brand: brand ?? DEFAULT_BRAND,
      });
    }

    if (tdsData?.tds_server_trans_id) {
      const tdsParam = {
        transaction_id: tdsData.tds_server_trans_id
      };

      Object.assign(creditCard, {
        threed_secure: tdsParam,
      });
    }
    
    if (riskId) {
      Object.assign(creditCard, {
        risk_id: riskId,
      });
    }

    details.credit_card = creditCard;
  }

  const payment = buildPaymentPayload(paymentMethod, {
    details,
    quoteId: isDeliveryEnabled ? quoteId : undefined,
  });

  return { payment };
}

export function get3dsParam(
  name: string | undefined,
  address: any,
  creditCard: any,
  phoneNumber: any,
  countryCode: any,
  email: string,
  documentType: string,
  documentNumber: string,
  total: any
) {
  const date = creditCard.expirationDate?.split('/');

  const newParam = {
    payments: [
      {
        payment_method: 'credit_card',
        credit_card: {
          card: {
            number: creditCard.cardNumber?.replace(/\s/g, ''),
            holder_name: creditCard.cardHolder,
            holder_document: creditCard.cpf ? parseCpf(creditCard.cpf) : creditCard.cpf,
            exp_month: date && Number(date[0]),
            exp_year: date && Number(`20${date[1]}`),
            cvv: creditCard.cvv,
            brand: creditCard.brand,
            billing_address: {
              country: address.address.country,
              state: address.address.state,
              city: address.address.city,
              zip_code: address.address.zip_code,
              line_1: `${address.address.number}, ${address.address.street}, ${address.address.neighborhood}`,
              line_2: address.address.complement || '',
            },
          },
        },
        amount: total.amount,
      },
    ],
    shipping: {
      address: {
        country: address.address.country,
        state: address.address.state,
        city: address.address.city,
        zip_code: address.address.zip_code,
        line_1: `${address.address.number}, ${address.address.street}, ${address.address.neighborhood}`,
        line_2: address.address.complement || '',
      },
      recipient_name: name,
    },
    customer: {
      name: name,
      type: 'individual',
      email: email,
      document: documentType === 'cnpj'
        ? documentNumber?.replace(/[^A-Z0-9]/gi, '').toUpperCase()
        : parseCpf(documentNumber ?? ''),
      document_type: documentType,
      phones: {
        mobile_phone: {
          country_code: countryCode?.replace(/[^0-9]/g, ''),
          area_code: phoneNumber?.replace(/[^0-9]/g, '').slice(0, 2),
          number: phoneNumber?.replace(/[^0-9]/g, '').slice(2),
        },
      },
    }
  };

  return newParam;
}
